import React, { useState, useMemo } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { FaFileExcel, FaTimes, FaCheckCircle } from "react-icons/fa";
import Navbar from "../Navbar";
import Footer from "../Footer";
import { useOutlet } from "../../context/OutletContext";
import { CustomModal } from "../Modal/CustomModal";
import { MenuItemsGroupedTable, MenuItemsSummaryFooter } from "./ModalSubComponents";

const baseUrl = process.env.REACT_APP_BASE_URL;

const isBlank = (val) => val === undefined || val === null || String(val).trim() === "";

const parseAmount = (raw) => {
    if (isBlank(raw)) return 0;
    if (typeof raw === "number") return raw;
    const cleaned = String(raw).replace(/,/g, "").trim();
    const num = parseFloat(cleaned);
    return isNaN(num) ? 0 : num;
};

const parseMenuWorkbook = (workbook) => {
    const items = [];

    workbook.SheetNames.forEach((sheetName) => {
        const worksheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            defval: "",
        });

        if (rows.length === 0) return;

        // First row is the header ("", "Cost Price", "Selling Price") — skip it.
        let currentDescription = "";

        for (let r = 1; r < rows.length; r++) {
            const row = rows[r] || [];
            const name = row[0];
            const costRaw = row[1];
            const sellingRaw = row[2];

            if (isBlank(name) && isBlank(costRaw) && isBlank(sellingRaw)) {
                continue; // fully empty row
            }

            const costBlank = isBlank(costRaw);
            const sellingBlank = isBlank(sellingRaw);

            if (costBlank && sellingBlank) {
                // Category / description row, e.g. "Appetizers"
                currentDescription = String(name).trim();
                continue;
            }

            if (isBlank(name)) continue; // price with no name — skip

            items.push({
                Name: String(name).trim(),
                Description: currentDescription,
                Type: sheetName,
                Price: parseAmount(sellingRaw),
                costPrice: parseAmount(costRaw),
                discountExempt: "no",
                serviceChargeExempt: "no",
            });
        }
    });

    return items;
};

// ── Component ────────────────────────────────────────────────────────────────

const MenuUpload = () => {
    const { outletOptions, fullOutlets, loading: outletsLoading } = useOutlet();

    const [fileName, setFileName] = useState("");
    const [menuItems, setMenuItems] = useState([]);
    const [parseError, setParseError] = useState(null);

    const [applyToAll, setApplyToAll] = useState(false);
    const [selectedOutlets, setSelectedOutlets] = useState([]); // array of outlet names

    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submittingState, setSubmittingState] = useState(null); // "saved" | "published" | null

    const [uploadResults, setUploadResults] = useState(null);
    const [showResultsModal, setShowResultsModal] = useState(false);

    const hasData = menuItems.length > 0;

    const effectiveOutlets = useMemo(
        () => (applyToAll ? outletOptions : selectedOutlets),
        [applyToAll, outletOptions, selectedOutlets]
    );

    const totalPayloadCount = menuItems.length * effectiveOutlets.length;

    const resetAll = () => {
        setFileName("");
        setMenuItems([]);
        setParseError(null);
        setApplyToAll(false);
        setSelectedOutlets([]);
        setShowPreviewModal(false);
    };

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setParseError(null);
        setFileName(file.name);

        try {
            const buffer = await file.arrayBuffer();
            const workbook = XLSX.read(buffer, { type: "array" });

            const parsed = parseMenuWorkbook(workbook);

            if (parsed.length === 0) {
                setParseError("No valid menu items were found in this file.");
                setMenuItems([]);
                return;
            }

            setMenuItems(parsed);
            setShowPreviewModal(true);
            toast.success(`Parsed ${parsed.length} item(s) from ${file.name}`);
        } catch (err) {
            console.error(err);
            setParseError(err.message || "Failed to parse the file");
            setMenuItems([]);
            toast.error(err.message || "Failed to parse the file");
        }

        e.target.value = "";
    };

    const toggleOutlet = (outletName) => {
        setApplyToAll(false);
        setSelectedOutlets((prev) =>
            prev.includes(outletName)
                ? prev.filter((o) => o !== outletName)
                : [...prev, outletName]
        );
    };

    const handleApplyToAllClick = () => {
        setApplyToAll((prev) => !prev);
        setSelectedOutlets([]);
    };

    const canSubmit = hasData && effectiveOutlets.length > 0 && !submitting;

    const buildItemsPayload = () =>
        menuItems.map((item) => ({
            Name: item.Name,
            Description: item.Description,
            Type: item.Type,
            Price: item.Price,
            discountExempt: item.discountExempt,
            serviceChargeExempt: item.serviceChargeExempt,
        }));

    const handleSubmit = async (state) => {
        if (!canSubmit) return;

        setSubmitting(true);
        setSubmittingState(state);

        const token = localStorage.getItem("token");
        const items = buildItemsPayload();

        const settled = await Promise.allSettled(
            effectiveOutlets.map((outletName) =>
                axios.post(`${baseUrl}/menu-forversioning`, {
                    token,
                    outlet: outletName,
                    state,
                    items,
                })
            )
        );

        const results = {};
        let successCount = 0;

        settled.forEach((result, i) => {
            const outletName = effectiveOutlets[i];

            if (result.status === "fulfilled") {
                results[outletName] = { success: true };
                successCount += 1;
            } else {
                const err = result.reason;
                results[outletName] = {
                    success: false,
                    error:
                        err?.response?.data?.error ||
                        err?.response?.data?.message ||
                        err?.message ||
                        "Failed to upload",
                };
            }
        });

        setUploadResults(results);
        setShowResultsModal(true);
        setSubmitting(false);
        setSubmittingState(null);

        if (successCount === effectiveOutlets.length) {
            toast.success(
                `${state === "published" ? "Published" : "Saved"} menu for ${successCount} outlet(s)`
            );
        } else if (successCount === 0) {
            toast.error("Failed to upload menu for all selected outlet(s)");
        } else {
            toast.error(
                `${successCount}/${effectiveOutlets.length} outlet(s) succeeded — see details`
            );
        }
    };

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            <Navbar />

            <div className="flex-1 p-3 max-w-5xl mx-auto w-full">
                <div className="text-lg font-semibold text-gray-800 mb-1">
                    Upload Menu
                </div>

                {/* Upload + preview */}
                <div className="mb-3 flex items-center gap-4">
                    <label className="flex items-center gap-1.5 px-3 py-2 bg-emerald-100 text-emerald-800 rounded-md font-medium text-sm cursor-pointer hover:bg-emerald-200 transition">
                        <FaFileExcel size={14} />
                        Upload Menu (.xlsx)
                        <input
                            type="file"
                            accept=".xlsx,.xls"
                            className="hidden"
                            onChange={handleFileChange}
                        />
                    </label>

                    {hasData && (
                        <div
                            onClick={() => setShowPreviewModal(true)}
                            className="flex items-center gap-4 px-3 py-2 bg-white border border-gray-200 rounded-md text-sm cursor-pointer hover:bg-gray-50 hover:shadow-sm transition"
                        >
                            <div className="flex flex-col leading-tight">
                                <span className="font-medium text-gray-800 max-w-[220px] truncate">
                                    {fileName}
                                </span>
                                <span className="text-xs text-gray-500">
                                    {menuItems.length} item(s)
                                </span>
                            </div>

                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    resetAll();
                                }}
                                className="p-1.5 rounded-full bg-gray-100 text-gray-500 hover:bg-red-100 hover:text-red-600 transition"
                                title="Clear uploaded data"
                            >
                                <FaTimes size={10} />
                            </button>
                        </div>
                    )}

                    {parseError && (
                        <span className="text-sm text-red-600">{parseError}</span>
                    )}
                </div>

                {hasData && (
                    <div className="bg-white border border-gray-200 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-base font-semibold text-gray-700">
                                Select Outlets
                            </div>

                            <button
                                type="button"
                                onClick={handleApplyToAllClick}
                                className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${applyToAll
                                    ? "bg-blue-600 text-white hover:bg-blue-700"
                                    : "bg-blue-100 text-blue-800 hover:bg-blue-200"
                                    }`}
                            >
                                {applyToAll ? "Applying to all outlets" : "Apply to all"}
                            </button>
                        </div>

                        {outletsLoading ? (
                            <div className="text-sm text-gray-500">Loading outlets...</div>
                        ) : outletOptions.length === 0 ? (
                            <div className="text-sm text-gray-500">No outlets found.</div>
                        ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                {fullOutlets.map((outlet) => {
                                    const isSelected =
                                        applyToAll || selectedOutlets.includes(outlet.name);

                                    return (
                                        <label
                                            key={outlet.id}
                                            className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm cursor-pointer transition ${isSelected
                                                ? "border-blue-500 bg-blue-50"
                                                : "border-gray-200 bg-gray-50 hover:bg-gray-100"
                                                } ${applyToAll ? "opacity-70 cursor-not-allowed" : ""}`}
                                        >
                                            <input
                                                type="checkbox"
                                                className="accent-blue-600"
                                                checked={isSelected}
                                                disabled={applyToAll}
                                                onChange={() => toggleOutlet(outlet.name)}
                                            />
                                            <span className="text-gray-800">{outlet.name}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        )}

                        <div className="mt-4 flex items-center justify-between">
                            <div className="text-sm text-gray-600">
                                {effectiveOutlets.length > 0 ? (
                                    <span>
                                        {menuItems.length} item(s) × {effectiveOutlets.length}{" "}
                                        outlet(s) ={" "}
                                        <span className="font-semibold text-gray-800">
                                            {totalPayloadCount}
                                        </span>{" "}
                                        record(s) will be sent
                                    </span>
                                ) : (
                                    <span>Select at least one outlet, or click "Apply to all"</span>
                                )}
                            </div>

                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => handleSubmit("saved")}
                                    disabled={!canSubmit}
                                    className="px-4 py-2 rounded-md font-medium bg-gray-100 text-gray-800 hover:bg-gray-200 hover:shadow-md text-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {submitting && submittingState === "saved"
                                        ? "Saving..."
                                        : "Save"}
                                </button>

                                <button
                                    type="button"
                                    onClick={() => handleSubmit("published")}
                                    disabled={!canSubmit}
                                    className="px-4 py-2 rounded-md font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 hover:shadow-md text-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {submitting && submittingState === "published"
                                        ? "Publishing..."
                                        : "Save & Publish"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Preview modal */}
            <CustomModal
                show={showPreviewModal}
                onHide={() => setShowPreviewModal(false)}
                size="lg"
                centered
                scrollable
            >
                <CustomModal.Header
                    closeButton
                    onHide={() => setShowPreviewModal(false)}
                >
                    <CustomModal.Title className="text-base">Menu Preview</CustomModal.Title>
                </CustomModal.Header>

                <CustomModal.Body>
                    <MenuItemsGroupedTable items={menuItems} />
                </CustomModal.Body>

                <CustomModal.Footer>
                    <MenuItemsSummaryFooter items={menuItems} />
                </CustomModal.Footer>
            </CustomModal>

            {/* Upload results modal — per-outlet success/error */}
            <CustomModal
                show={showResultsModal}
                onHide={() => setShowResultsModal(false)}
                size="lg"
                centered
                scrollable
            >
                <CustomModal.Header
                    closeButton
                    onHide={() => setShowResultsModal(false)}
                >
                    <CustomModal.Title className="text-base">Upload Results</CustomModal.Title>
                </CustomModal.Header>

                <CustomModal.Body>
                    {uploadResults && (
                        <div className="space-y-2 mb-4">
                            {Object.entries(uploadResults).map(([outletName, result]) => (
                                <div
                                    key={outletName}
                                    className={`flex items-start justify-between gap-3 px-3 py-2.5 rounded-md border text-sm ${result.success
                                        ? "border-green-200 bg-green-50"
                                        : "border-red-200 bg-red-50"
                                        }`}
                                >
                                    <div className="flex items-start gap-2">
                                        {result.success ? (
                                            <FaCheckCircle
                                                size={13}
                                                className="text-green-600 mt-0.5 shrink-0"
                                            />
                                        ) : (
                                            <FaTimes
                                                size={13}
                                                className="text-red-600 mt-0.5 shrink-0"
                                            />
                                        )}
                                        <div>
                                            <div className="font-medium text-gray-800">
                                                {outletName}
                                            </div>
                                            {!result.success && (
                                                <div className="text-xs text-red-700 mt-0.5">
                                                    {result.error}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <span
                                        className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${result.success
                                            ? "bg-green-100 text-green-700"
                                            : "bg-red-100 text-red-700"
                                            }`}
                                    >
                                        {result.success ? "Success" : "Failed"}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="mb-1.5 pt-3 border-t border-gray-200 text-sm font-semibold text-gray-700">
                        Uploaded items
                    </div>
                    <MenuItemsGroupedTable items={menuItems} />
                </CustomModal.Body>

                <CustomModal.Footer>
                    <MenuItemsSummaryFooter items={menuItems} />
                </CustomModal.Footer>
            </CustomModal>

            <Footer />
        </div>
    );
};

export default MenuUpload;